import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Radio, TableCell, TableRow, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import { SystemSwitch } from "../../../../../components/UIToolkit/Clickable/SystemSwitch";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { CONFIG_TABLE_BODY_CELL_SX, CONFIG_TABLE_BODY_ROW_SX } from "../../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { ALPHA, BADGE, FONT_SIZE, ICON_SIZE, INK, JOB_SETTINGS_EDIT } from "../../../../../theme/tokens";
import type { JobEditRow } from "./useJobsEditState";

type Props = {
	row: JobEditRow;
	/** Saved values are still loading, so existing jobs show a spinner. */
	isLoading: boolean;
	/** Saved values failed to load, so existing jobs' switches are unavailable instead of guessed. */
	isError: boolean;
	onSetPrimaryJob: (jobId: string) => void;
	onSetSilent: (isSilent: boolean) => void;
	onRemove: () => void;
};

/** One row in the edit-mode Job table: name, primary, notifications switch and remove. */
export const JobsEditRow: FC<Props> = ({ row, isLoading, isError, onSetPrimaryJob, onSetSilent, onRemove }) => {
	const { t } = useTranslation();
	const isKnown = row.isSilent !== undefined;
	const isNewAndSilent = row.isNew && row.isSilent === true;

	return (
		<TableRow
			data-testid={`profile-job-settings-row-${row.id}`}
			sx={[
				...(Array.isArray(CONFIG_TABLE_BODY_ROW_SX) ? CONFIG_TABLE_BODY_ROW_SX : [CONFIG_TABLE_BODY_ROW_SX]),
				row.isNew && ((theme) => ({ backgroundColor: alpha(theme.palette.warning.main, ALPHA.HINT_TINT) })),
			]}
		>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: row.isSilent ? 500 : 600, color: row.isSilent ? INK.MUTED : INK.PRIMARY }}>
						{row.displayName}
					</Typography>
					{row.isNew && <MetaChip label={t("jobNotifications.profile.table.new")} tone="warning" dataTestId={`profile-job-settings-new-${row.id}`} />}
				</Box>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX} align="center">
				<Radio
					size="small"
					checked={row.isPrimary}
					onChange={() => onSetPrimaryJob(row.id)}
					slotProps={{ input: { "aria-label": t("jobNotifications.profile.table.makePrimary", { job: row.displayName }) } }}
					data-testid={`profile-job-settings-primary-${row.id}`}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				{/* New jobs start muted, so the ring points to the switch that turns them on. */}
				<Box
					sx={[
						{ display: "inline-flex", borderRadius: BADGE.PILL_RADIUS_FULL },
						isNewAndSilent &&
							((theme) => ({
								outline: `${JOB_SETTINGS_EDIT.NEW_SWITCH_RING_PX}px solid ${alpha(theme.palette.warning.main, ALPHA.STATUS_BORDER)}`,
								outlineOffset: JOB_SETTINGS_EDIT.NEW_SWITCH_RING_OFFSET_PX,
							})),
					]}
				>
					<SystemSwitch
						checked={row.isSilent === false}
						onChange={(event) => onSetSilent(!event.target.checked)}
						label={isKnown ? t(row.isSilent ? "jobNotifications.profile.table.off" : "jobNotifications.profile.table.on") : undefined}
						ariaLabel={t("jobNotifications.profile.notificationsFor", { job: row.displayName })}
						loading={!isKnown && isLoading}
						disabled={!isKnown && isError}
						dataTestId={`profile-job-settings-notify-${row.id}`}
					/>
				</Box>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX} align="right">
				<SystemButton
					iconButton
					buttonVariant="borderless"
					noFullWidthMobile
					onClick={onRemove}
					aria-label={t("jobNotifications.profile.table.removeJob", { job: row.displayName })}
					data-testid={`profile-job-settings-remove-${row.id}`}
				>
					<CloseRoundedIcon sx={{ fontSize: ICON_SIZE.CONTROL }} />
				</SystemButton>
			</TableCell>
		</TableRow>
	);
};
